import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LanguageToggle } from './LandingChat';
import { C } from '../lib/theme';

export default function ClinicLanding() {
  const { t } = useTranslation();
  const [messages, setMessages] = useState([{ role: 'assistant', content: t('clinic_landing.vera_intro') }]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [msgCount, setMsgCount] = useState(0);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, typing]);

  async function handleSend() {
    if (!input.trim() || typing) return;
    const text = input.trim();
    setInput('');
    const newMessages = [...messages, { role: 'user', content: text }];
    setMessages(newMessages);
    setTyping(true);

    try {
      const res = await fetch('/api/clinic-chat', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ messages: newMessages.map(m => ({ role: m.role, content: m.content })) }),
      });
      const data = await res.json();
      setMessages([...newMessages, { role: 'assistant', content: data.text || t('clinic_landing.error_retry') }]);
      setMsgCount(c => c + 1);
    } catch {
      setMessages([...newMessages, { role: 'assistant', content: t('common.error_connection') }]);
    } finally {
      setTyping(false);
    }
  }

  return (
    <div style={{ maxWidth: 480, margin: '0 auto', height: '100vh', display: 'flex', flexDirection: 'column', background: C.cream, fontFamily: 'sans-serif' }}>

      <div style={{ background: C.carbon, padding: '1rem 1.2rem', display: 'flex', alignItems: 'center', gap: '0.7rem', flexShrink: 0 }}>
        <img src="/logo192.png" style={{ width: '1.8rem', height: '1.8rem', objectFit: 'contain' }} alt="Vera" />
        <div style={{ flex: 1 }}>
          <div style={{ color: '#fff', fontFamily: 'Georgia, serif', fontWeight: 700, fontSize: '1.1rem' }}>{t('clinic_landing.header_title')}</div>
          <div style={{ color: '#9CA3AF', fontSize: '0.75rem' }}>{t('clinic_landing.header_subtitle')}</div>
        </div>
        <LanguageToggle light />
        <button
          onClick={() => window.location.href = '/'}
          style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, color: '#9CA3AF', fontSize: '0.72rem', padding: '0.35rem 0.7rem', cursor: 'pointer' }}
        >
          {t('clinic_landing.back_btn')}
        </button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
        {messages.map((msg, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start', alignItems: 'flex-end', gap: '0.4rem' }}>
            {msg.role === 'assistant' && <img src="/logo192.png" style={{ width: '1.3rem', height: '1.3rem', objectFit: 'contain', flexShrink: 0 }} alt="" />}
            <div style={{
              maxWidth: '80%',
              padding: '0.8rem 1rem',
              borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
              background: msg.role === 'user' ? C.carbon : '#fff',
              color: msg.role === 'user' ? '#fff' : C.carbon,
              fontSize: '0.92rem', lineHeight: 1.6,
              boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
              whiteSpace: 'pre-line',
            }}>
              {msg.content}
            </div>
          </div>
        ))}

        {typing && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <img src="/logo192.png" style={{ width: '1.3rem', height: '1.3rem', objectFit: 'contain', flexShrink: 0 }} alt="" />
            <div style={{ background: '#fff', borderRadius: '16px 16px 16px 4px', padding: '0.8rem 1rem', boxShadow: '0 1px 6px rgba(0,0,0,0.07)', color: C.gray, fontSize: '0.88rem', fontStyle: 'italic' }}>
              {t('clinic_landing.typing')}
            </div>
          </div>
        )}

        {msgCount >= 2 && !typing && (
          <div style={{ background: '#fff', borderRadius: 16, padding: '1.2rem', boxShadow: '0 2px 16px rgba(44,106,79,0.12)', border: `2px solid ${C.sage}`, marginTop: '0.4rem' }}>
            <p style={{ margin: '0 0 0.8rem', color: C.carbon, fontSize: '0.92rem', lineHeight: 1.5 }}>
              <strong>{t('clinic_landing.cta_bold')}</strong><br/>
              {t('clinic_landing.cta_sub')}
            </p>
            <button
              onClick={() => window.location.href = '/?page=clinic-signup'}
              style={{ width: '100%', padding: '0.85rem', borderRadius: 8, border: 'none', background: C.sage, color: '#fff', fontWeight: 700, fontSize: '1rem', cursor: 'pointer' }}
            >
              {t('clinic_landing.cta_btn')}
            </button>
            <p style={{ margin: '0.6rem 0 0', fontSize: '0.75rem', color: C.gray, textAlign: 'center' }}>
              {t('clinic_landing.cta_price')}
            </p>
          </div>
        )}

        <div ref={endRef} />
      </div>

      <div style={{ padding: '0.75rem', borderTop: '1px solid #E5E7EB', background: C.cream, flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
            placeholder={t('clinic_landing.placeholder')}
            style={{ flex: 1, padding: '0.7rem 1rem', borderRadius: 24, border: `1.5px solid ${C.mint}`, background: '#fff', outline: 'none', fontSize: '0.95rem' }}
          />
          <button
            onClick={handleSend}
            style={{ width: 44, height: 44, borderRadius: '50%', border: 'none', background: C.carbon, color: '#fff', fontSize: '1.2rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            →
          </button>
        </div>
      </div>
    </div>
  );
}
